import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// layouts
import MainLayout from 'src/layouts/main';
import UnsubscribeLayout from 'src/layouts/unsubscribe-layout';
import CandidateFormLayout from 'src/layouts/candidateForm-layout';
import EmployeeFormLayout from 'src/layouts/employeeForm-layout';
import thankyouLayout from 'src/layouts/thank-you-layout';

import SimpleLayout from 'src/layouts/simple';
import CompactLayout from 'src/layouts/compact';
// components
import { SplashScreen } from 'src/components/loading-screen';
import { CandidateCreateView } from 'src/sections/recruitment/candidate/view';
import CandidateReplicaCreateView from 'src/sections/recruitment/candidate/view/candidateReplicaFormView';
import EmployeeReplicaCreateView from 'src/sections/Employee/view/employee-replica-create-view';
import { Layout as LandingPageLayout } from 'src/LandingUi/LandingLayout';

// ----------------------------------------------------------------------

export const HomePage = lazy(() => import('src/pages/home'));
const Page500 = lazy(() => import('src/pages/500'));
const ErrorPPage = lazy(() => import('src/pages/error-page'));
const Page403 = lazy(() => import('src/pages/403'));
const Page404 = lazy(() => import('src/pages/404'));
const FaqsPage = lazy(() => import('src/pages/faqs'));
const AboutPage = lazy(() => import('src/pages/about-us'));
const ContactPage = lazy(() => import('src/pages/contact-us'));
const PricingPage = lazy(() => import('src/pages/pricing'));
const PaymentPage = lazy(() => import('src/pages/payment'));
const CheckoutPage = lazy(() => import('src/pages/checkout'));
const ComingSoonPage = lazy(() => import('src/pages/coming-soon'));
const MaintenancePage = lazy(() => import('src/pages/maintenance'));
const UnsubscribePage = lazy(() => import('src/pages/dashboard/unsubscribe/UnsubscribePage'));
const CandidateCreatePage = lazy(() => import('src/pages/dashboard/recruitment/candidate/new'));
const ThankYouPage = lazy(() => import('src/pages/dashboard/thankyou/ThankYouPage'));
// tenant reg import
const TenantRegPage = lazy(() => import('src/pages/tenant-reg'));
const TenantRegistrationPage = lazy(() => import('src/pages/tenant-registration'));
// tenant finder (workspace discovery)
const TenantFinderPage = lazy(() => import('src/LandingUi/screens/TenantFinderPage'));

// PRODUCT
const ProductListPage = lazy(() => import('src/pages/product/list'));
const ProductDetailsPage = lazy(() => import('src/pages/product/details'));
const ProductCheckoutPage = lazy(() => import('src/pages/product/checkout'));
// BLOG
const PostListPage = lazy(() => import('src/pages/post/list'));
const PostDetailsPage = lazy(() => import('src/pages/post/details'));

// ----------------------------------------------------------------------

export const mainRoutes = [
  {
    element: (
      <MainLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      { path: 'about-us', element: <AboutPage /> },
      { path: 'contact-us', element: <ContactPage /> },
      { path: 'faqs', element: <FaqsPage /> },
      // tenant reg path
      {
        path: 'tenant-reg',
        element: <TenantRegPage />,
      },
      {
        path: 'tenant-registration',
        element: <TenantRegistrationPage />,
      },

      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'checkout', element: <ProductCheckoutPage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <PostListPage />, index: true },
          { path: 'list', element: <PostListPage /> },
          { path: ':title', element: <PostDetailsPage /> },
        ],
      },
    ],
  },
  {
    element: (
      <SimpleLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </SimpleLayout>
    ),
    children: [
      { path: 'pricing', element: <PricingPage /> },
      { path: 'payment', element: <PaymentPage /> },
      { path: 'checkout', element: <CheckoutPage /> },
    ],
  },
  {
    element: (
      <CompactLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </CompactLayout>
    ),
    children: [
      { path: 'coming-soon', element: <ComingSoonPage /> },
      { path: 'maintenance', element: <MaintenancePage /> },
      { path: '500', element: <Page500 /> },
      { path: '404', element: <Page404 /> },
      { path: '403', element: <Page403 /> },
      { path: 'error-page', element: <ErrorPPage /> },
    ],
  },
  {
    path: 'unsubscribe',
    element: (
      <UnsubscribeLayout>
        <Suspense fallback={<SplashScreen />}>
          <UnsubscribePage />
        </Suspense>
      </UnsubscribeLayout>
    ),
  },
  {
    path: 'submitted',
    element: (
      <thankyouLayout>
        <Suspense fallback={<SplashScreen />}>
          <ThankYouPage />
        </Suspense>
      </thankyouLayout>
    ),
  },
  {
    path: '/candidateForm/:id?',
    element: (
      <CandidateFormLayout>
        <Suspense fallback={<SplashScreen />}>
          <CandidateReplicaCreateView />
        </Suspense>
      </CandidateFormLayout>
    ),
  },
  {
    path: '/vendorCandidateForm/:id?',
    element: (
      <CandidateFormLayout>
        <Suspense fallback={<SplashScreen />}>
          <CandidateReplicaCreateView />
        </Suspense>
      </CandidateFormLayout>
    ),
  },
  {
    path: 'employeeForm/:id?',
    element: (
      <EmployeeFormLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </EmployeeFormLayout>
    ),
    children: [
      {
        path: 'candidate/:id?',
        element: (
          <Suspense fallback={<SplashScreen />}>
            <EmployeeReplicaCreateView type="candidate" />
          </Suspense>
        ),
      },
      {
        path: 'employee/:id',
        element: (
          <Suspense fallback={<SplashScreen />}>
            <EmployeeReplicaCreateView type="employee" />
          </Suspense>
        ),
      },
    ],
  },
  // ── Tenant Finder (Find Workspace) ──
  // Uses the Landing page layout (same navbar / footer as the marketing site)
  {
    path: 'find-workspace',
    element: (
      <LandingPageLayout isMinimal hideCTA>
        <Suspense fallback={<SplashScreen />}>
          <TenantFinderPage />
        </Suspense>
      </LandingPageLayout>
    ),
  },
];

